// Shopping Cart Context
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, setDoc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../lib/firebase';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  // Load cart from Firestore or localStorage
  useEffect(() => {
    if (currentUser) {
      loadCartFromFirestore();
    } else {
      loadCartFromLocalStorage();
    }
  }, [currentUser]);

  // Load cart from Firestore for logged-in users
  const loadCartFromFirestore = async () => {
    try {
      setLoading(true);
      const cartDoc = await getDoc(doc(db, 'carts', currentUser.uid));
      if (cartDoc.exists()) {
        setCart(cartDoc.data().items || {});
      } else {
        // Migrate from localStorage if exists
        const localCart = localStorage.getItem('aurumnivy_cart');
        if (localCart) {
          const items = JSON.parse(localCart);
          setCart(items);
          await saveCartToFirestore(items);
          localStorage.removeItem('aurumnivy_cart');
        }
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load cart from localStorage for guests
  const loadCartFromLocalStorage = () => {
    try {
      const localCart = localStorage.getItem('aurumnivy_cart');
      if (localCart) {
        setCart(JSON.parse(localCart));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  };

  // Save cart to Firestore
  const saveCartToFirestore = async (items) => {
    if (!currentUser) return;
    try {
      await setDoc(doc(db, 'carts', currentUser.uid), {
        items,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  };

  // Save cart to localStorage
  const saveCartToLocalStorage = (items) => {
    try {
      localStorage.setItem('aurumnivy_cart', JSON.stringify(items));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Add item to cart
  const addToCart = async (product, quantity = 1) => {
    const newCart = { ...cart };
    if (newCart[product.id]) {
      newCart[product.id].quantity += quantity;
    } else {
      newCart[product.id] = {
        ...product,
        quantity
      };
    }
    setCart(newCart);

    if (currentUser) {
      await saveCartToFirestore(newCart);
    } else {
      saveCartToLocalStorage(newCart);
    }
  };

  // Update item quantity
  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      return removeFromCart(productId);
    }

    const newCart = { ...cart };
    if (newCart[productId]) {
      newCart[productId].quantity = quantity;
      setCart(newCart);

      if (currentUser) {
        await saveCartToFirestore(newCart);
      } else {
        saveCartToLocalStorage(newCart);
      }
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId) => {
    const newCart = { ...cart };
    delete newCart[productId];
    setCart(newCart);

    if (currentUser) {
      await saveCartToFirestore(newCart);
    } else {
      saveCartToLocalStorage(newCart);
    }
  };

  // Clear cart
  const clearCart = async () => {
    setCart({});
    if (currentUser) {
      await setDoc(doc(db, 'carts', currentUser.uid), { items: {} });
    } else {
      localStorage.removeItem('aurumnivy_cart');
    }
  };

  // Get cart items as array
  const getCartItems = () => {
    return Object.values(cart);
  };

  // Get cart total
  const getCartTotal = () => {
    return Object.values(cart).reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  // Get cart count
  const getCartCount = () => {
    return Object.values(cart).reduce((count, item) => {
      return count + item.quantity;
    }, 0);
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartItems,
    getCartTotal,
    getCartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
